import {
  Button,
  SequenceMenu,
  SequenceMenuDivider,
  SetIsOpen,
  useNotifications,
  IconEmail,
  IconEyeOpen,
  Flex
} from '@sequencehq/core-components'
import { hasCreditNoteBeenApplied } from '@sequencehq/utils'
import { CreditNoteIssueDialog } from 'CreditNotes/components/dialog/CreditNoteIssue'
import type { CreditNoteStatus, LineItem } from 'CreditNotes/types'
import { CREDIT_NOTE_STATUSES } from 'CreditNotes/constants'
import {
  DeleteCreditNoteMenuItem,
  MarkAsSentCreditNoteMenuItem,
  SendTestCreditNoteMenuItem,
  VoidCreditNoteMenuItem
} from 'CreditNotes/menuItems'
import { useCreditNoteQuery } from 'CreditNotes/hooks/useCreditNote.ts'
import { ViewCustomerPortalButton } from 'components/ViewCustomerPortalButton/ViewCustomerPortalButton.tsx'
import { ProtectedButton, usePermissions } from 'lib/rbac'
import { useFeatureFlags } from 'hooks/useFeatureFlags'
import { useCreditNoteRevRecWarning } from './creditNoteRevRecWarning'
import { match } from 'ts-pattern'
import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { ButtonDownloadCreditNotePdf } from './ButtonDownloadCreditNotePdf'
import { ButtonRecalculateCreditNote } from './ButtonRecalculateCreditNote'
import { ApplyCreditNoteDrawer } from './applyCreditNote/ApplyCreditNoteDrawer'
import { hasPositiveBalance } from './applyCreditNote/applyCreditNote.helpers'

interface Props {
  creditNoteId: string
  creditNoteNumber: string | undefined
  creditNoteStatus: CreditNoteStatus
  onPreviewCreditNotePDF: () => void
  incompleteLineItems: LineItem[]
  hasNegativeTotal: boolean
  onEditLineItem: (id: string) => void
  onTriggerValidation: (triggered: boolean) => void
}

export const CreditNoteDetailsHeaderActions = ({
  creditNoteId,
  creditNoteNumber,
  creditNoteStatus,
  onPreviewCreditNotePDF,
  incompleteLineItems,
  hasNegativeTotal,
  onEditLineItem,
  onTriggerValidation
}: Props) => {
  const { data: creditNote } = useCreditNoteQuery(creditNoteId)
  const [showIssueDialog, setShowIssueDialog] = useState(false)
  const [showApplyDrawer, setShowApplyDrawer] = useState(false)

  const navigate = useNavigate()
  const { displayNotification } = useNotifications()
  const displayRevRecWarning = useCreditNoteRevRecWarning()

  const { can } = usePermissions()
  const flags = useFeatureFlags()

  useEffect(() => {
    if (incompleteLineItems.length === 0) {
      onTriggerValidation(false)
    }
  }, [incompleteLineItems.length, onTriggerValidation])

  const isDraft = creditNoteStatus === CREDIT_NOTE_STATUSES.DRAFT
  const isSent = creditNoteStatus === CREDIT_NOTE_STATUSES.SENT
  const isFinal = creditNoteStatus === CREDIT_NOTE_STATUSES.FINAL
  const isInProgress = creditNoteStatus === CREDIT_NOTE_STATUSES.IN_PROGRESS
  const hasIncompleteItems = incompleteLineItems.length > 0
  const isApplied = hasCreditNoteBeenApplied(creditNote?.applicationStatus)
  // Incomplete line items take priority so the user resolves those first; the
  // negative-total guard mirrors the backend finalisation check.
  const sendBlockedReason = match({ hasIncompleteItems, hasNegativeTotal })
    .with({ hasIncompleteItems: true }, () => 'incompleteItems' as const)
    .with({ hasNegativeTotal: true }, () => 'negativeTotal' as const)
    .otherwise(() => null)
  const canRecalculate =
    (isDraft || isInProgress) &&
    Boolean(creditNote?.billingScheduleId) &&
    can('org.creditNote:update')

  // A credit note whose credit became a cash grant has nothing left to spend
  // elsewhere; an absent or zero balance hides the button too.
  const canApplyCredits =
    flags.onLedger &&
    isSent &&
    creditNote?.settings?.generateCashCreditGrant !== 'ENABLED' &&
    hasPositiveBalance(creditNote?.balance)

  const customerPortalPath = `/customers/${creditNote?.customerId}/credit-notes/${creditNoteId}`

  const sendTestItems =
    isDraft && can('org.creditNote:send')
      ? [
          () => (
            <SendTestCreditNoteMenuItem
              key="send-test"
              creditNoteId={creditNoteId}
            />
          )
        ]
      : []

  const statusChangeItems = [
    ...((isDraft || isFinal) && can('org.creditNote:update')
      ? [
          () => (
            <MarkAsSentCreditNoteMenuItem
              key="mark-as-sent"
              creditNoteId={creditNoteId}
            />
          )
        ]
      : []),
    ...((isDraft || isSent) && !isApplied && can('org.creditNote:update')
      ? [
          ({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
            <VoidCreditNoteMenuItem
              key="void"
              creditNoteId={creditNoteId}
              onClose={() => setIsOpen?.(false)}
            />
          )
        ]
      : []),
    ...((isDraft || isInProgress) && can('org.creditNote:update')
      ? [
          ({ setIsOpen }: { setIsOpen?: SetIsOpen }) => (
            <DeleteCreditNoteMenuItem
              key="delete"
              creditNoteId={creditNoteId}
              onClose={() => setIsOpen?.(false)}
              onDeleted={() => navigate('/credit-notes')}
            />
          )
        ]
      : [])
  ]

  const handleBlockedSend = (reason: 'incompleteItems' | 'negativeTotal') =>
    match(reason)
      .with('incompleteItems', () => {
        onTriggerValidation(true)
        displayRevRecWarning({
          incompleteLineItems,
          onEdit: onEditLineItem,
          useRevRec: flags.useRevRec
        })
      })
      .with('negativeTotal', () => {
        displayNotification("Credit note total can't be negative", {
          type: 'error',
          description:
            'Adjust the line items so the credit total is at least zero before sending.',
          id: 'credit-note-negative-total-warning',
          'data-testid': 'creditNote.negativeTotalWarningBanner'
        })
      })
      .exhaustive()

  const needsDivider = sendTestItems.length > 0 && statusChangeItems.length > 0

  const kebabItems = [
    ...sendTestItems,
    ...(needsDivider
      ? [<SequenceMenuDivider key="divider-after-send-test" />]
      : []),
    ...statusChangeItems
  ]

  return (
    <Flex gap={2} alignItems="center" alignContent="center">
      {canRecalculate && (
        <ButtonRecalculateCreditNote creditNoteId={creditNoteId} />
      )}
      <ButtonDownloadCreditNotePdf
        creditNoteId={creditNoteId}
        creditNoteNumber={creditNoteNumber}
        creditNoteStatus={creditNoteStatus}
      />
      <Button
        data-testid="creditNote.previewPdf"
        variant="secondary"
        leadingIcon={<IconEyeOpen />}
        onClick={onPreviewCreditNotePDF}
      >
        Preview credit note PDF
      </Button>

      {[CREDIT_NOTE_STATUSES.SENT, CREDIT_NOTE_STATUSES.FINAL].includes(
        creditNoteStatus
      ) && <ViewCustomerPortalButton path={customerPortalPath} />}

      {isDraft && !sendBlockedReason && (
        <>
          <ProtectedButton permission="org.creditNote:send">
            <Button
              leadingIcon={<IconEmail />}
              onClick={() => setShowIssueDialog(true)}
              data-testid="creditNote.sendButton"
            >
              Issue and send
            </Button>
          </ProtectedButton>
          {showIssueDialog && (
            <CreditNoteIssueDialog
              creditNoteId={creditNoteId}
              onClose={() => setShowIssueDialog(false)}
            />
          )}
        </>
      )}
      {isDraft && sendBlockedReason && (
        <ProtectedButton permission="org.creditNote:send">
          <Button
            leadingIcon={<IconEmail />}
            onClick={() => handleBlockedSend(sendBlockedReason)}
            data-testid="creditNote.sendButton"
          >
            Issue and send
          </Button>
        </ProtectedButton>
      )}
      {canApplyCredits && (
        <ProtectedButton permission="org.creditNote:update">
          <Button
            onClick={() => setShowApplyDrawer(true)}
            data-testid="creditNote.applyCreditsButton"
          >
            Apply credits
          </Button>
        </ProtectedButton>
      )}
      {/* Outside canApplyCredits so a balance refetch can't unmount an open drawer. */}
      {showApplyDrawer && (
        <ApplyCreditNoteDrawer
          creditNoteId={creditNoteId}
          onClose={() => setShowApplyDrawer(false)}
        />
      )}
      {kebabItems.length > 0 && (
        <SequenceMenu
          data-testid="creditNote.kebabMenu"
          variant="ghost"
          width={226}
          alignment="bottom-right"
          items={kebabItems}
        />
      )}
    </Flex>
  )
}
